import { isArray, isNil, fromPairs } from 'lodash';

import { SubPurposes } from '../formHelper.js';
import * as textUtil from '../textUtil';
import { isDateDisabled } from '../date.js';
import moment from 'moment';

// -------------------- Validation Error Messages --------------------
const MessageMap = {
  current: {
    required: '入力必須項目です',
    minWords: '10文字以上の入力が必要です',
    maxWords: '10文字以下の入力にする必要があります',
    minCount: '10個以上の選択にする必要があります',
    maxCount: '10個以下の選択にする必要があります',
    phoneNum: '正しい電話番号の書式ではないようです',
    onlyNumbers: '数字以外の文字が含まれています',
    mailAddress: '正しいEメールアドレスの書式ではないようです',
    date: '正しい日付の書式ではないようです',
    dateRange: '無効な日付',
    zipcode: '正しい郵便番号の書式ではないようです',
    onlyPhonetic: '全角カタカナで入力してください',
  }
};

const MessageMapKeys = Object.keys(MessageMap.current);

export const updateMessageMap = (obj) => {
  const msgObj = fromPairs(
    Object.entries(obj).filter(
      ([key, value]) => !!value && MessageMapKeys.includes(key)
    )
  );
  MessageMap.current = {
    ...MessageMap.current,
    ...msgObj
  };
};

export const errorMessages = {
  required: () => MessageMap.current.required,
  minWords: num => textUtil.replaceNumber(MessageMap.current.minWords, num),
  maxWords: num => textUtil.replaceNumber(MessageMap.current.maxWords, num),
  minCount: num => textUtil.replaceNumber(MessageMap.current.minCount, num),
  maxCount: num => textUtil.replaceNumber(MessageMap.current.maxCount, num),
  phoneNum: () => MessageMap.current.phoneNum,
  onlyNumbers: () => MessageMap.current.onlyNumbers,
  mailAddress: () => MessageMap.current.mailAddress,
  date: () => MessageMap.current.date,
  dateRange: () => MessageMap.current.dateRange,
  zipcode: () => MessageMap.current.zipcode,
  onlyPhonetic: () => MessageMap.current.onlyPhonetic,
};

// -------------------- Validators --------------------
const phoneExcludeRE = /[-ー()]/gi;
const phoneNumRE = /^([0|０][0-9０-９]{9,13})$/;
const phoneNumberAreaCodeRE = /^0[0-9]+$/;
const mailAddressRE = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i
const japaneseCharRE = /^[ぁ-んァ-ン一-龥ー]+$/
export const dateRE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const numbersRE = /^[0-9]+$/;
export const zipcodeRE = /^[0-9]{3}-[0-9]{4}$/;
export const zipcodeZoneRE = /^[0-9]{3}$/;
export const zipcodeAreaRE = /^[0-9]{4}$/;

const noValidate = () => null;

const required = isRequired => value => {
  if (!isRequired) {
    return null;
  }

  if (isArray(value)) {
    return value.length === 0
      ? errorMessages.required()
      : null;
  } else {
    return isNil(value) || value === ""
      ? errorMessages.required()
      : null;
  }
};

const phoneNumber = phoneNum =>
  phoneNum && phoneNum.replace(phoneExcludeRE, '').match(phoneNumRE)
    ? null
    : errorMessages.phoneNum();

const phoneNumberAreaCode = str =>
  phoneNumberAreaCodeRE.test(str)
    ? null
    : errorMessages.phoneNum();

const onlyNumbers = str =>
  numbersRE.test(str)
    ? null
    : errorMessages.onlyNumbers();

const mailAddress = str =>
  str && str.match(mailAddressRE)
    ? null
    : errorMessages.mailAddress();

const date = values => str => {
  const day = moment(str)
  if (!str) return null;
  if (!str.match(dateRE)) return errorMessages.date();

  if (values && isDateDisabled({day, validations: values})) return errorMessages.dateRange();
  return null
}

const maxLength = max => value =>
  value && value.length > max
    ? errorMessages.maxWords(max)
    : null;

const minLength = min => value =>
  value && value.length < min
    ? errorMessages.minWords(min)
    : null;

const maxCount = max => value =>
  value.length > max
    ? errorMessages.maxCount(max)
    : null;

const minCount = min => value =>
  value.length < min
    ? errorMessages.minCount(min)
    : null;

const zipcode = value =>
  value && value.match(zipcodeRE)
    ? null
    : errorMessages.zipcode();

const zipcodeZone = value =>
  value && value.match(zipcodeZoneRE)
    ? null
    : errorMessages.zipcode();

const zipcodeArea = value =>
  value && value.match(zipcodeAreaRE)
    ? null
    : errorMessages.zipcode();

const onlyPhoneticChar = str =>
  str && str.match(japaneseCharRE)
    ? null
    : errorMessages.onlyPhonetic();

// -------------------- Validation Utils --------------------
const validationCase = {
  required: (value) => required(value),
  min_words: (value) => minLength(value),
  max_words: (value) => maxLength(value),
  min_count: (value) => minCount(value),
  max_count: (value) => maxCount(value),
  only_numbers: () => onlyNumbers,

  short_text: () => noValidate,
  mail_address: () => mailAddress,
  phone_number: () => phoneNumber,
  date: (values) => date(values),

  [SubPurposes.PhoneNumber.AreaCode]: () => phoneNumberAreaCode,
  [SubPurposes.PhoneNumber.CityCode]: () => onlyNumbers,
  [SubPurposes.PhoneNumber.SubscriberNumber]: () => onlyNumbers,
  [SubPurposes.Address.Zipcode]: () => zipcode,
  [SubPurposes.Address.ZipcodeZone]: () => zipcodeZone,
  [SubPurposes.Address.ZipcodeArea]: () => zipcodeArea,
  [SubPurposes.FullName.FirstNamePhonetic]: () => onlyPhoneticChar,
  [SubPurposes.FullName.LastNamePhonetic]: () => onlyPhoneticChar,
}

export const getValidation = (name, value) => {
  return (validationCase[name] || (() => noValidate))(value)
}
